"use client";

import { c as _c } from "react/compiler-runtime";
import { useEmailComposer } from "../../context";
import { ImageUrlForm } from "../../editor/image-url-form";
import { normalizeImageHref } from "../../utils";
import { storedAttributeProps } from "../html-attributes";
import { NodeViewWrapper } from "@tiptap/react";
import { useCallback, useRef } from "react";
import { LuImage } from "react-icons/lu";
import { Skeleton } from "@/components/ui/skeleton";
import { cx } from "@/lib/tailwind-utils";
import { getImagePlaceholderStyles, getImageStyles } from "./styles";
interface ImageAttrs {
  src: string;
  alt: string;
  width: "auto" | "25%" | "50%" | "75%" | "100%";
  alignment: "left" | "center" | "right";
  maxHeight: string;
  href: string;
  uploading: boolean;
  naturalWidth: number;
  naturalHeight: number;
  [key: string]: unknown;
}
interface Props {
  attrs: ImageAttrs;
  updateAttrs: (attrs: Partial<ImageAttrs>) => void;
  selected: boolean;
}
export function ImageTiptapView(t0) {
  const $ = _c(43);
  const {
    attrs,
    updateAttrs,
    selected
  } = t0;
  const {
    onImageUpload,
    styleSettings
  } = useEmailComposer();
  const fileInputRef = useRef(null);
  let t1;
  if ($[0] !== onImageUpload || $[1] !== updateAttrs) {
    t1 = async file => {
      if (!onImageUpload) {
        return;
      }
      const bmp = await createImageBitmap(file);
      updateAttrs({
        uploading: true,
        naturalWidth: bmp.width,
        naturalHeight: bmp.height
      });
      bmp.close();
      try {
        const url = await onImageUpload(file);
        updateAttrs({
          src: url,
          alt: file.name,
          uploading: false
        });
      } catch {
        updateAttrs({
          uploading: false
        });
      }
    };
    $[0] = onImageUpload;
    $[1] = updateAttrs;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleFileSelect = t1;
  let t2;
  if ($[3] !== handleFileSelect) {
    t2 = e => {
      const file_0 = e.target.files?.[0];
      if (file_0) {
        handleFileSelect(file_0);
      }
      e.target.value = "";
    };
    $[3] = handleFileSelect;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleInputChange = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      left: "0",
      center: "0 auto",
      right: "0 0 0 auto"
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const marginMap = t3;
  if (!attrs.src && attrs.uploading) {
    const hasNaturalSize = attrs.naturalWidth > 0 && attrs.naturalHeight > 0;
    const t4 = attrs.width === "auto" ? hasNaturalSize ? attrs.naturalWidth : 200 : attrs.width;
    const t5 = hasNaturalSize ? `${attrs.naturalWidth} / ${attrs.naturalHeight}` : "16 / 9";
    const t6 = marginMap[attrs.alignment];
    let t7;
    if ($[6] !== t4 || $[7] !== t5 || $[8] !== t6) {
      t7 = <NodeViewWrapper data-block-type="emailImage"><Skeleton className="rounded" style={{
          width: t4,
          maxWidth: "100%",
          aspectRatio: t5,
          display: "block",
          margin: t6
        }} /></NodeViewWrapper>;
      $[6] = t4;
      $[7] = t5;
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  if (!attrs.src) {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = getImagePlaceholderStyles();
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex flex-col items-center gap-2 p-4" onClick={() => fileInputRef.current?.click()}><LuImage className="h-8 w-8" /><span className="text-body">Click to upload an image</span></div>;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== updateAttrs) {
      t6 = <ImageUrlForm className="w-full max-w-sm px-4 pb-4" onSubmit={src => updateAttrs({
        src,
        alt: ""
      })} />;
      $[12] = updateAttrs;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== handleInputChange) {
      t7 = <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleInputChange} />;
      $[14] = handleInputChange;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== t6 || $[17] !== t7) {
      t8 = <NodeViewWrapper data-block-type="emailImage"><div className="flex flex-col" style={t4}>{t5}{t6}{t7}</div></NodeViewWrapper>;
      $[16] = t6;
      $[17] = t7;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    return t8;
  }
  let T0;
  let normalizedHref;
  let t4;
  let t5;
  if ($[19] !== attrs.href || $[20] !== selected) {
    normalizedHref = normalizeImageHref(attrs.href);
    T0 = NodeViewWrapper;
    t5 = "emailImage";
    t4 = cx("relative", normalizedHref && "cursor-pointer", selected && "ring-2 ring-primary/30 rounded");
    $[19] = attrs.href;
    $[20] = selected;
    $[21] = T0;
    $[22] = normalizedHref;
    $[23] = t4;
    $[24] = t5;
  } else {
    T0 = $[21];
    normalizedHref = $[22];
    t4 = $[23];
    t5 = $[24];
  }
  const t6 = normalizedHref || undefined;
  const t7 = attrs.src;
  const t8 = attrs.alt;
  let t9;
  if ($[25] !== attrs) {
    t9 = storedAttributeProps(attrs);
    $[25] = attrs;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== attrs || $[28] !== styleSettings) {
    t10 = getImageStyles(attrs, styleSettings);
    $[27] = attrs;
    $[28] = styleSettings;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== attrs.alt || $[31] !== attrs.src || $[32] !== t10 || $[33] !== t9) {
    t11 = <img src={t7} alt={t8} {...t9} style={t10} draggable={false} />;
    $[30] = attrs.alt;
    $[31] = attrs.src;
    $[32] = t10;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t11 || $[36] !== t4 || $[37] !== t6) {
    t12 = <div className={t4} title={t6}>{t11}</div>;
    $[35] = t11;
    $[36] = t4;
    $[37] = t6;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== T0 || $[40] !== t12 || $[41] !== t5) {
    t13 = <T0 data-block-type={t5}>{t12}</T0>;
    $[39] = T0;
    $[40] = t12;
    $[41] = t5;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
}